import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import { generateReceiptHeader, getCollegeInfo } from './receiptHeaderUtils';
import { ensureRupeeFont, formatINR } from './jspdfFonts';
import { formatDate } from '../utils/dateTimeUtils';
applyPlugin(jsPDF);

export const generateStudentLedgerPDF = async (ledgerData, collegeInfo = null) => {
  const doc = new jsPDF();

  const resolvedFont = await ensureRupeeFont(doc);
  const CURRENCY = resolvedFont === 'FreeSans' ? '\u20B9' : 'Rs.';
  // Get college info - prefer parameter over localStorage
  if (!collegeInfo) {
    collegeInfo = getCollegeInfo() || {};
    console.warn('generateStudentLedgerPDF: collegeInfo not passed, using localStorage fallback. This may cause tenant isolation issues.');
  }
  let yPos = await generateReceiptHeader(doc, collegeInfo, 'STUDENT LEDGER');

  // Date
  doc.setFontSize(9);
  doc.text(`Generated: ${formatDate(new Date())}`, 14, yPos);
  yPos += 10;

  // Student Information
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Student Information', 14, yPos);
  
  yPos += 8;
  doc.setFontSize(10);
  
  const studentInfo = [
    ['Student Name', ledgerData.student.full_name || 'N/A'],
    ['Admission Number', ledgerData.student.admission_number || 'N/A'],
    ['Course', ledgerData.student.course_name || 'N/A'],
    ['Session', ledgerData.student.session || 'N/A'],
    ['Mobile', ledgerData.student.mobile || 'N/A'],
    ['Email', ledgerData.student.email || 'N/A']
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: studentInfo,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 50, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 130, fontStyle: 'normal' }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Transaction History
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Transaction History', 14, yPos);
  
  yPos += 5;

  // Prepare table data
  const tableData = ledgerData.transactions.map(txn => {
    const date = txn.date ? formatDate(txn.date) : 'N/A';
    const type = txn.type === 'payment' ? 'Payment' : 'Fee Charge';
    const debit = txn.debit > 0 ? txn.debit.toLocaleString('en-IN') : '-';
    const credit = txn.credit > 0 ? txn.credit.toLocaleString('en-IN') : '-';
    const balance = txn.balance.toLocaleString('en-IN');
    
    return [
      date,
      type,
      txn.description,
      debit,
      credit,
      balance
    ];
  });

  doc.autoTable({
    startY: yPos,
    head: [['Date', 'Type', 'Description', 'Debit (₹)', 'Credit (₹)', 'Balance (₹)']],
    body: tableData,
    theme: 'striped',
    headStyles: { 
      fillColor: [41, 128, 185], 
      textColor: 255,
      fontStyle: 'bold',
      fontSize: 9
    },
    styles: { fontSize: 8,
      cellPadding: 2
    , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 22 },
      1: { cellWidth: 20 },
      2: { cellWidth: 60 },
      3: { cellWidth: 25, halign: 'right', textColor: [220, 38, 38] },
      4: { cellWidth: 25, halign: 'right', textColor: [22, 163, 74] },
      5: { cellWidth: 25, halign: 'right', fontStyle: 'bold' }
    },
    didParseCell: function(data) {
      // Color code the Type column
      if (data.column.index === 1 && data.cell.section === 'body') {
        if (data.cell.raw === 'Payment') {
          data.cell.styles.fillColor = [220, 252, 231];
        } else {
          data.cell.styles.fillColor = [254, 226, 226];
        }
      }
    }
  });

  yPos = doc.lastAutoTable.finalY + 15;

  // Summary Section
  doc.setFontSize(12);
  doc.setFont(resolvedFont, 'bold');
  doc.text('Financial Summary', 14, yPos);
  
  yPos += 5;

  const summaryData = [
    ['Total Fee Charged', `${CURRENCY} ${formatINR(ledgerData.summary.total_debits)}`],
    ['Total Paid', `${CURRENCY} ${formatINR(ledgerData.summary.total_credits)}`],
    ['Outstanding Balance', `${CURRENCY} ${formatINR(ledgerData.summary.outstanding_balance)}`]
  ];

  doc.autoTable({
    startY: yPos,
    head: [],
    body: summaryData,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 3 , font: resolvedFont },
    columnStyles: {
      0: { cellWidth: 100, fontStyle: 'bold', textColor: [100, 100, 100] },
      1: { cellWidth: 70, halign: 'right', fontStyle: 'bold', fontSize: 11 }
    },
    didParseCell: function(data) {
      if (data.column.index === 1 && data.row.index === 0) {
        data.cell.styles.textColor = [220, 38, 38];
      } else if (data.column.index === 1 && data.row.index === 1) {
        data.cell.styles.textColor = [22, 163, 74];
      } else if (data.column.index === 1 && data.row.index === 2) {
        data.cell.styles.textColor = [234, 88, 12];
      }
    }
  });

  // Footer
  const pageHeight = doc.internal.pageSize.height;
  const pageWidth = doc.internal.pageSize.width;
  doc.setFontSize(8);
  doc.setFont(resolvedFont, 'italic');
  doc.text('This is a computer-generated document.', pageWidth / 2, pageHeight - 20, { align: 'center' });
  doc.text('Fee Management System', pageWidth / 2, pageHeight - 15, { align: 'center' });

  // Save the PDF
  doc.save(`Student_Ledger_${ledgerData.student.admission_number || 'Report'}.pdf`);

  return doc;
};
